import { createFileRoute } from "@tanstack/react-router";

import { COMPANY, COMPANY_ADDRESS_LINE } from "@/lib/company";
import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = `${BASE_URL}/terms`;
const TITLE = "Terms of Service | Adkiro";
const DESCRIPTION =
  "The terms covering use of adkiro.com, the tools on it, and a connected Meta advertising account — who owns what, what is promised, and what is not.";
const UPDATED = "30 September 2026";

interface Section {
  id?: string;
  heading: string;
  body: string[];
  points?: string[];
}

const SECTIONS: Section[] = [
  {
    heading: "Who these terms are with",
    body: [
      `Adkiro is the trading name of ${COMPANY.legalName} (${COMPANY.legalNameLocal}), a ${COMPANY.legalForm}, UIC ${COMPANY.uic}, with its registered office at ${COMPANY_ADDRESS_LINE}. In these terms, “Adkiro” means that company and “you” means anyone using this website, the tools on it, or the Meta account connection.`,
      `Using the site means accepting what is written here. If something does not work for you, say so before engaging rather than after — ${COMPANY.email}.`,
      "A signed engagement agreement, where one exists, takes precedence over this page. These terms cover the website and the connection; they are not the terms of a paid service.",
    ],
  },
  {
    heading: "What Adkiro provides",
    body: [
      "Adkiro plans and runs Meta advertising for property agencies, brokers and developers, and publishes guides alongside that work. Paid services — an account review, a property launch campaign or monthly management — are agreed in writing, with their price, before any work begins.",
      "One-off services are paid in advance online through Stripe, which processes the payment and emails the receipt; card details never reach Adkiro. Monthly plans are paid the same way and renew by card each month, with a three-month minimum term, until cancelled in writing; an invoice is available on request. Advertising spend is paid by you directly to Meta and is never part of Adkiro's fee.",
      "The guides on this site are exactly that: free, and offered without warranty. They are a starting point for a conversation, not a substitute for a review of your specific account.",
    ],
  },
  {
    heading: "What is not promised",
    body: [
      "No advertising outcome is guaranteed, by Adkiro or by anyone honest. Results depend on your offer, your pricing, your market, your landing pages, seasonality and the auction — most of which sit outside any agency's control.",
      "Nothing on this site, including any figure in a guide or a reply from the assistant, is a forecast of what your account will do. Historical performance described anywhere on this site is not a prediction.",
    ],
  },
  {
    heading: "Connecting a Meta account",
    body: [
      "Connecting a Meta advertising account is optional. It requests the ads_read permission and nothing else, which allows Adkiro to read advertising performance data. It cannot create, edit, pause or delete anything in your account, and it does not grant Adkiro the ability to spend your budget.",
      "You must have the authority to grant that access for the account you connect. If the account belongs to a client or an employer, get their agreement first.",
      "Connecting is separate from granting Adkiro partner access to manage advertising. Management access is granted by you inside Meta Business Manager, is scoped to the assets you choose, and is revocable by you at any time without involving Adkiro.",
    ],
    points: [
      "Permission requested: ads_read only, which is read-only.",
      "Access tokens are encrypted before storage and are never displayed, logged or sent to a browser.",
      "You can revoke the connection at any moment in Meta's Business Integrations settings; access stops immediately.",
      `Deletion can also be requested through Meta, which triggers the callback at /api/meta/data-deletion, or directly at ${COMPANY.email}.`,
    ],
  },
  {
    heading: "Who owns what",
    body: [
      "You keep ownership of your advertising assets, without exception: your Business Portfolio, ad accounts, Pages, Instagram accounts, Pixels, datasets, audiences and creative. Adkiro works through partner access rather than asking anyone to transfer ownership, and that does not change when an engagement ends.",
      "Creative produced by Adkiro during a paid engagement belongs to you once it has been paid for. Adkiro's own methods, templates, internal tooling and the content published on this site remain Adkiro's.",
      "The guides, tools and written material on this site may be read, quoted with attribution, and applied to your own account freely. They may not be republished as someone else's work or resold.",
    ],
  },
  {
    heading: "Using the site sensibly",
    body: [
      "The tools here are provided for their intended purpose. Automated scraping, attempts to access areas that are not public, attempts to overload the service, or use of the assistant to generate unrelated content are not acceptable use, and access may be withdrawn without notice.",
      "The assistant on this site answers questions about Meta advertising and about Adkiro. It is a language model working from a defined set of information. It can be wrong, and it is not advice you should act on without checking — particularly anything touching your budget.",
    ],
  },
  {
    heading: "Liability",
    body: [
      "The site and its guides are provided as they are. To the extent the law allows, Adkiro is not liable for loss arising from decisions taken on the basis of free material published here, including advertising spend committed or withheld.",
      "This does not limit liability for anything that cannot lawfully be limited, and it does not apply to the obligations in a signed engagement agreement, which are governed by that agreement.",
    ],
  },
  {
    heading: "Changes, law and contact",
    body: [
      "These terms may change as the service changes. The date below shows when they were last revised, and material changes affecting an active engagement will be raised with you directly rather than left to be noticed.",
      "Bulgarian law governs these terms, and the courts of Sofia have jurisdiction over any dispute arising from them. If you are a consumer in the European Union, the mandatory protections of your own country's law are unaffected, and the European Commission's online dispute resolution platform remains available to you.",
      `Anything unclear, and anything you disagree with, goes to ${COMPANY.email}. A question asked before an engagement is cheaper for both sides than a disagreement during one.`,
    ],
  },
  {
    id: "legal-notice",
    heading: "Legal notice",
    body: [
      "Provider information for this website, as required of service providers established in the European Union.",
    ],
    points: [
      `Legal name: ${COMPANY.legalName} (${COMPANY.legalNameLocal}), a ${COMPANY.legalForm}. Adkiro is its trading name.`,
      `Registration: ${COMPANY.register}, UIC (ЕИК) ${COMPANY.uic}.`,
      `VAT number: ${COMPANY.vat}.`,
      `Registered office: ${COMPANY_ADDRESS_LINE}.`,
      `Managing director: ${COMPANY.managingDirector}.`,
      `Email: ${COMPANY.email}.`,
      `Telephone: ${COMPANY.phone}.`,
    ],
  },
];

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${BASE_URL}/logo.png?v=20260907` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader tone="ink" />

      <main>
        <section data-tone="ink" className="tone-ink">
          <div className="mx-auto max-w-3xl px-5 pb-12 pt-14 lg:px-8 lg:pt-20">
            <div className="hero-eyebrow">
              <span className="hero-eyebrow__line" />
              <span>Terms of service</span>
            </div>
            <h1 className="mt-7 text-[clamp(2.1rem,4.6vw,3.2rem)] leading-[1.04]">
              What is agreed, and what is not promised
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Short, and written to be read. Where a signed engagement agreement exists, that
              document governs the work — this page covers the site, the tools and the connection.
            </p>
            <p className="mt-7 text-[0.68rem] uppercase tracking-[0.16em] text-muted-foreground">
              Last updated {UPDATED}
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
          {SECTIONS.map((section) => (
            <section key={section.heading} id={section.id} className="mb-12 scroll-mt-24 last:mb-0">
              <h2 className="text-2xl leading-tight sm:text-[1.7rem]">{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph} className="mt-5 leading-relaxed text-muted-foreground">
                  {paragraph}
                </p>
              ))}
              {section.points && (
                <ul className="mt-6 space-y-3 border-l-2 border-primary/30 pl-5">
                  {section.points.map((point) => (
                    <li
                      key={point}
                      className="text-[0.95rem] leading-relaxed text-muted-foreground"
                    >
                      {point}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section className="mt-14 border-t border-border pt-8">
            <p className="text-sm leading-relaxed text-muted-foreground">
              See also the{" "}
              <a href="/privacy" className="text-primary hover:text-foreground">
                privacy and data processing notice
              </a>
              , which covers what is collected, how long it is kept, and how to have it deleted.
            </p>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
